import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { createDefaultFilename } from './filename-utility';

const ABORT_ERROR_MESSAGE = '__DOWNLOAD_FILES_AS_ZIP_ABORTED__';

const normalizeBlobFromResponse = async (response) => {
	if (response?.data instanceof Blob) {
		return response.data;
	}

	if (typeof response?.blob === 'function') {
		return response.blob();
	}

	return response?.data ?? null;
};

/**
 * Downloads multiple files from API calls and saves them as a single zip archive.
 *
 * @param {*} param0
 * @returns
 */
export async function downloadFilesAsZipFromApi({
	files,
	executeApiCall,
	zipFilename,
	defaultFilenamePrefix,
	onError,
}) {
	try {
		if (!Array.isArray(files) || !files.length) {
			return false;
		}

		if (typeof executeApiCall !== 'function') {
			throw new Error('executeApiCall must be provided for zip download');
		}

		const zip = new JSZip();

		await Promise.all(
			files.map(async (file, index) => {
				const response = await executeApiCall(file, index);

				if (response?.isAborted) {
					throw new Error(ABORT_ERROR_MESSAGE);
				}

				if (response?.isError) {
					throw new Error(response.error?.message || 'Download failed');
				}

				const blob = await normalizeBlobFromResponse(response);

				if (!(blob instanceof Blob)) {
					throw new Error('Download failed: file response was not a Blob');
				}

				const fallbackName = `file-${index + 1}`;
				const filename =
					file?.fileName || file?.filename || file?.name || fallbackName;

				zip.file(filename, blob);
			})
		);

		const zipContent = await zip.generateAsync({ type: 'blob' });
		const resolvedZipFilename =
			zipFilename || createDefaultFilename(defaultFilenamePrefix, 'zip');
		saveAs(zipContent, resolvedZipFilename);

		return true;
	} catch (err) {
		if (err?.message === ABORT_ERROR_MESSAGE) {
			return false;
		}

		if (!onError) {
			throw err;
		}

		onError(err);
		return false;
	}
}
